import { KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import { X } from 'ts-react-feather-icons';

import { Button } from './button';
import { useStyles } from './custom-data-grid-filter-panel.styles';

const CustomDataGridFilterOperators = ['contains', 'equals', 'startsWith', 'endsWith'];

export const CustomDataGridFilterPanel = () => {
  const classes = useStyles();
  const apiRef = useGridApiContext();
  const columns: GridColDef[] = apiRef.current.getAllColumns().filter((col) => col.filterable);

  const [localFilters, setLocalFilters] = useState<GridFilterModel>(
    apiRef.current.state.filter.filterModel,
  );

  const onLogicOperatorChange = (event: any) => {
    localFilters.logicOperator = event.target.value;
    setLocalFilters({ ...localFilters });
  };

  const onColumnSelectChange = (index: number, event: any) => {
    const existingItem: GridFilterItem = localFilters.items[index];
    existingItem.field = event.target.value;
    updateFilterItem(index, existingItem);
  };

  const onOperatorSelectChange = (index: number, event: any) => {
    const existingItem: GridFilterItem = localFilters.items[index];
    existingItem.operator = event.target.value;
    updateFilterItem(index, existingItem);
  };

  const onValueSelectChange = (index: number, event: any) => {
    const existingItem: GridFilterItem = localFilters.items[index];
    existingItem.value = event.target.value;
    updateFilterItem(index, existingItem);
  };

  const isCurrentFilterComplete = (index: number): boolean => {
    if (!localFilters?.items || !localFilters.items[index - 1]) {
      return false;
    }
    const filterItem = localFilters.items[index - 1];
    return filterItem.field && filterItem.operator && filterItem.value;
  };

  const addFilterItem = () => {
    if (!isCurrentFilterComplete(localFilters.items.length)) {
      return;
    }
    const newFilterItem: GridFilterItem = {
      id: localFilters?.items?.length ?? 0,
      field: '',
      operator: 'contains',
      value: '',
    };
    setLocalFilters((prev) => ({
      ...prev,
      items: [...(prev?.items ?? []), newFilterItem],
    }));
  };

  const removeFilterItem = (event: MouseEvent, index: number) => {
    event.stopPropagation();
    if (localFilters?.items && localFilters.items.length === 1) {
      localFilters.logicOperator = undefined;
      clearFilters();
      return;
    }
    setLocalFilters((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateFilterItem = (index: number, updatedItem: Partial<GridFilterItem>) => {
    setLocalFilters((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, ...updatedItem } : item)),
    }));
  };

  const applyFilters = () => {
    if (!isCurrentFilterComplete(getFilterItemsLength())) {
      return;
    }
    apiRef.current.setFilterModel(localFilters);
    apiRef.current.hideFilterPanel();
  };

  const clearFilters = () => {
    setLocalFilters({
      items: [{ id: 0, field: '', operator: 'contains', value: '' }],
    });
    apiRef.current.setFilterModel({
      items: [],
    });
    apiRef.current.hideFilterPanel();
  };

  const handleOnKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilters();
    }
  };

  const filterModelRow = (index: number) => {
    if (!localFilters?.items) {
      return;
    }

    const row = localFilters.items[index];

    return (
      <Box className={classes.formContainer} key={index}>
        {index > 0 && (
          <FormControl
            className={classes.logicOperatorFormControl}
            fullWidth
            margin="normal"
            variant="outlined"
            disabled={index > 1}
          >
            <Select
              labelId={`logic-operator-label-${index}`}
              id={`logic-operator-${index}`}
              displayEmpty
              size="small"
              value={localFilters.logicOperator || 'and'}
              onChange={(e) => onLogicOperatorChange(e)}
            >
              <MenuItem value={'and'}>and</MenuItem>
              <MenuItem value={'or'}>or</MenuItem>
            </Select>
          </FormControl>
        )}
        <Box className={classes.filterOptionsContainer}>
          <FormControl className={classes.firstRowFormControl} fullWidth margin="normal">
            <InputLabel id={`column-label-${index}`}>Column</InputLabel>
            <Select
              labelId={`column-label-${index}`}
              id={`column-${index}`}
              label="Column"
              value={row?.field || ''}
              onChange={(e) => onColumnSelectChange(index, e)}
              error={!row?.field}
            >
              {columns.map((column) => (
                <MenuItem key={column.field} value={column.field}>
                  {column.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl} fullWidth margin="normal">
            <InputLabel id={`operator-label-${index}`}>Operator</InputLabel>
            <Select
              labelId={`operator-label-${index}`}
              id={`operator-${index}`}
              label="Operator"
              defaultValue={row?.operator || 'contains'}
              onChange={(e) => onOperatorSelectChange(index, e)}
              error={!row?.operator}
            >
              {CustomDataGridFilterOperators.map((operator) => (
                <MenuItem key={operator} value={operator}>
                  {operator}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl} fullWidth margin="normal">
            <InputLabel id={`value-label-${index}`} />
            <TextField
              id={`value-label-${index}`}
              label="Value"
              variant="outlined"
              value={row?.value || ''}
              onChange={(e) => onValueSelectChange(index, e)}
              error={!row?.value}
            />
          </FormControl>
          <IconButton
            className={classes.removeFilterButton}
            data-cy="close-modal"
            aria-label="close"
            onClick={(event) => removeFilterItem(event, index)}
            size="large"
          >
            <X />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const getFilterModelRows = () => {
    if (!localFilters || localFilters.items.length === 0) {
      return filterModelRow(0);
    }
    return localFilters.items.map((_: GridFilterItem, index: number) => filterModelRow(index));
  };

  const getFilterItemsLength = () => {
    return localFilters?.items.length || 0;
  };

  useEffect(() => {
    if (apiRef.current.state.filter.filterModel.items.length > 0) {
      // an existing filterModel has been passed into the component
      return;
    }
    const initialGridFilterItem = {
      id: 0,
      field: '',
      operator: 'contains',
      value: '',
    };
    setLocalFilters({
      items: [initialGridFilterItem],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={classes.container} onKeyDown={handleOnKeyDown}>
      {getFilterModelRows()}
      <Box className={classes.actionsContainer}>
        <Box display="flex" justifyContent="flex-end">
          <Button
            disabled={!isCurrentFilterComplete(getFilterItemsLength())}
            className={classes.addFilterButton}
            size="small"
            onClick={() => {
              addFilterItem();
            }}
          >
            + Add Filter
          </Button>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Button
            color="primary"
            size="small"
            className={classes.addFilterButton}
            onClick={() => {
              clearFilters();
            }}
          >
            Clear Filters
          </Button>
          <Button
            disabled={!isCurrentFilterComplete(getFilterItemsLength())}
            color="primary"
            size="small"
            type="submit"
            className={classes.submitButton}
            onClick={() => {
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
